import * as Yup from "yup";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { FormikForm } from "components/FormikForm/FormikForm";
import Modal from "components/DesignSystem/Modal/Modal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useUpdateBookkeepingSettingsMutation } from "store/apis/booksLockDate";
import { BackendError } from "types/utils/error";
import { Label } from "components/DesignSystem/TextInput/TextInput";

const emailSchema = Yup.object().shape({
  emails: Yup.array()
    .of(Yup.string().email("Invalid email address"))
    .min(1, "At least one email address is mandatory"),
});

type EmailFormValues = {
  emails: string[];
};

interface EditFinancialsEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  emails: string[];
}

export const EditFinancialsEmailModal = ({
  isOpen,
  onClose,
  emails,
}: EditFinancialsEmailModalProps) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [updateBooksSetting, { isLoading }] =
    useUpdateBookkeepingSettingsMutation();

  const handleSubmit = async (values: EmailFormValues) => {
    try {
      await updateBooksSetting({
        entityId,
        completed_financials_email_recipients: values.emails,
      }).unwrap();
      successToast({ message: "Emails updated successfully" });
      onClose();
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update email recipients",
        },
        error as Error
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content useCustomOverlay>
        <FormikForm
          initialValues={{ emails }}
          validationSchema={emailSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, isValid }) => (
            <>
              <Modal.Header>
                <Modal.Title>Edit email</Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body>
                <Combobox
                  withForm
                  name="emails"
                  isMulti
                  creatable
                  options={[]}
                  value={values.emails.map((e) => ({ value: e, label: e }))}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  label={
                    <Label
                      className="t-block t-truncate"
                      htmlFor="emails"
                      required
                    >
                      Sent to
                    </Label>
                  }
                />
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Button customType="secondary" onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading || !isValid}
                >
                  Add
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
